import {
  Box,
  Button,
  Card,
  Divider,
  Fab,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useTheme } from "@emotion/react";
import { useNavigate, useParams } from "react-router-dom";
import {
  currencyFormat,
  toTwoDecimals,
} from "../../../../components/NewDetail/calculations/utils";
import moment from "moment";
import {
  Add,
  AttachMoney,
  Close,
  Delete,
  KeyboardArrowUp,
  PriceCheck,
  Send,
} from "@mui/icons-material";
import { SnackAlertContext } from "../../../../context/SnackAlertContext";
import ReceiveCashOrCheckPaymentDialog from "./ReceiveCashOrCheckPaymentDialog";
import SendInvoiceDialog from "../../Invoices/SendInvoiceDialog";
import InvoicePayment from "../../Invoices/InvoicePayment";
import CreateInvoiceDialog from "./CreateInvoiceDialog";
import PrintSalesMenuItem from "./PrintSalesMenuItem";
import ConfirmTicketPaidDialog from "./ConfirmTicketPaidDialog";
import { AuthContext } from "../../../../context/AuthContext";
import CancelTicketDialog from "./CancelTicketDialog";

const SalesTicket = () => {
  const { id } = useParams();
  const theme = useTheme();
  const { ezorder, authToken } = useContext(AuthContext);
  const { openSnackMessage } = useContext(SnackAlertContext);
  const navigate = useNavigate();
  const [salesTicket, setSalesTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionsAnchor, setActionsAnchor] = useState(null);

  // Invoice Stuff
  const [createInvoiceDialogOpen, setCreateInvoiceDialogOpen] = useState(false);
  const [createInvoiceError, setCreateInvoiceErrror] = useState("");
  const [sendInvoiceDialogOpen, setSendInvoiceDialogOpen] = useState(false);
  const [receivePaymentDialogOpen, setReceivePaymentDialogOpen] =
    useState(false);
  const [confirmPaidDialogOpen, setConfirmPaidDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const [invoiceLink, setInvoiceLink] = useState("");
  const [invoice, setInvoice] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [depositAccounts, setDepositAccounts] = useState([]);
  const [parsedCreateInvoiceData, setParsedCreateInvoiceData] = useState(null);

  const parseSalesTicketToInvoiceData = (salesTicket) => {
    let Line = [];
    for (let item of salesTicket.salesTicketItems) {
      Line.push({
        DetailType: "SalesItemLineDetail",
        Amount: Number(item.totalPrice),
        Description: item.description,
        SalesItemLineDetail: {
          Qty: Number(item.quantity),
          UnitPrice: Number(item.unitPrice),
          ServiceDate: moment(salesTicket.createdAt).format(),
          //   ItemRef: {
          //     name: item.service.Name,
          //     value: item.service.Id,
          //   },
          ItemRef: null,
          TaxCodeRef: {
            value: "TAX", //item.isTaxed ? "TAX" : "NON",
          },
        },
      });
    }

    setParsedCreateInvoiceData({
      CustomerRef: null,
      Line,
    });
  };

  const createInvoice = async (requestBody) => {
    try {
      setCreateInvoiceErrror("");
      setLoading(true);
      const response = await ezorder.post(
        "/quickbooks/sales-tickets/invoice",
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (response.status == 200 || response.status === 201) {
        openSnackMessage("success", "Invoice Created");
        getSalesTicketById(id); // refresh
        setCreateInvoiceDialogOpen(false);
      } else {
        if (
          response &&
          response.response &&
          response.response.data &&
          response.response.data.error
        ) {
          setCreateInvoiceErrror(response.response.data.error);
        } else {
          setCreateInvoiceErrror("Something went wrong");
        }
      }
    } catch (error) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.data?.error) {
          setCreateInvoiceErrror(error.response.data.error);
        } else {
          setCreateInvoiceErrror("Internal server error");
        }
      } else {
        setCreateInvoiceErrror("Internal server error");
      }
    } finally {
      setLoading(false);
    }
  };

  const getInvoiceDetails = async () => {
    try {
      setLoading(true);
      let data = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };

      const response = await ezorder.get(
        `/quickbooks/invoices/${salesTicket.invoiceId}`,
        data
      );

      setInvoice(response.data.invoice);
      setInvoiceLink(response.data.invoice.InvoiceLink);
    } catch (error) {
      console.log("Failed To Load Invoice for Sales Ticket", salesTicket.id);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethods = async (controller) => {
    try {
      setLoading(true);
      let data = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };
      if (controller) {
        data.signal = controller.signal;
      }

      const response = await ezorder.get(`/quickbooks/payment-methods`, data);

      setPaymentMethods(response.data.paymentMethods);
    } catch (error) {
      console.log("Failed To Load Payment Methods");
    } finally {
      setLoading(false);
    }
  };

  const getDepositAccounts = async (controller) => {
    try {
      setLoading(true);
      let data = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };
      if (controller) {
        data.signal = controller.signal;
      }

      const response = await ezorder.get(`/quickbooks/accounts`, data);

      setDepositAccounts(response.data.accounts);
    } catch (error) {
      console.log("Failed To Load Payment Methods");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseActionMenu = () => {
    setActionsAnchor(null);
  };

  const authHeader = {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  };

  const getSalesTicketById = async (id) => {
    try {
      setLoading(true);
      const response = await ezorder.get(`/sales-tickets/${id}`, authHeader);
      if (response.status === 200 || response.status == 201) {
        setSalesTicket(response.data.salesTicket);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      // quoteId is not null. Load the order and set it as quote detail
      if (!salesTicket) getSalesTicketById(id);
    }
  }, [id]);

  useEffect(() => {
    if (salesTicket) {
      getPaymentMethods();
      getDepositAccounts();
      parseSalesTicketToInvoiceData(salesTicket);
    }
  }, [salesTicket]);

  useEffect(() => {
    if (salesTicket && salesTicket.invoiceId) {
      getInvoiceDetails();
    }
  }, [salesTicket]);

  return (
    <Box>
      {salesTicket && (
        <Box>
          <Box>
            <Box sx={{ marginTop: "16px" }}>
              <Typography variant="h6">Details</Typography>
              <Box sx={{ padding: "16px" }}>
                <Typography>
                  Customer:{" "}
                  {salesTicket.isNotAnIndustryCustomer
                    ? ""
                    : salesTicket.company.name}
                </Typography>
                <Typography>
                  Created On:{" "}
                  {moment(salesTicket.created_at).format("MM/DD/YY")}
                </Typography>
                <Typography>Status: {salesTicket.status}</Typography>
              </Box>
            </Box>
            <Box sx={{ marginTop: "16px" }}>
              <Typography variant="h6">Items</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="left">Description</TableCell>
                      <TableCell align="left">UOM</TableCell>
                      <TableCell align="left">Quantity</TableCell>
                      <TableCell align="left">Price</TableCell>
                      <TableCell align="left">Item Total Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesTicket.salesTicketItems.map(
                      (salesTicketItem, index) => {
                        return (
                          <TableRow key={index}>
                            <TableCell align="left">
                              {salesTicketItem.description}
                            </TableCell>
                            <TableCell align="left">
                              {salesTicketItem.uom}
                            </TableCell>
                            <TableCell align="left">
                              {salesTicketItem.quantity ?? 0}
                            </TableCell>
                            <TableCell align="left">
                              {currencyFormat(salesTicketItem.unitPrice ?? 0)}
                            </TableCell>
                            <TableCell align="left">
                              {currencyFormat(salesTicketItem.totalPrice ?? 0)}
                            </TableCell>
                          </TableRow>
                        );
                      }
                    )}
                  </TableBody>
                </Table>
                <Box sx={{ marginTop: "16px" }}>
                  <Typography variant="h6">
                    Subtotal: {currencyFormat(salesTicket.subTotalPrice ?? 0)}
                  </Typography>
                  <Typography variant="h6">
                    Sales Tax: {currencyFormat(salesTicket.totalSalesTax ?? 0)}
                  </Typography>
                  <Typography variant="h6">
                    Total: {currencyFormat(salesTicket.totalPrice ?? 0)}
                  </Typography>
                </Box>
              </TableContainer>
            </Box>
          </Box>

          <Box sx={{ marginTop: "16px" }}>
            {/* <SalesTicketInvoice
              salesTicket={salesTicket}
              onInvoiceCreated={() => {
                getSalesTicketById(id); // refresh
              }}
            /> */}

            {salesTicket &&
              salesTicket.invoiceId &&
              salesTicket.invoiceCustomerRefName && (
                <Box sx={{ paddingBottom: "200px" }}>
                  <Typography variant="h5" sx={{}}>
                    Invoice
                  </Typography>
                  <Box sx={{ padding: "16px" }}>
                    {invoice ? (
                      <Box sx={{ display: "flex", flexDirection: "row" }}>
                        <Box sx={{}}>
                          <Typography sx={{ fontWeight: "bold" }}>
                            Details
                          </Typography>
                          Customer:{" "}
                          <Link
                            target="_blank"
                            rel="noopener noreferrer"
                            href={invoice.InvoiceLink}
                          >
                            {invoice.CustomerRef.name}
                          </Link>
                          <Typography>Invoice #{invoice.DocNumber}</Typography>
                          <Typography>
                            Invoice Date: {invoice.TxnDate}
                          </Typography>
                          <Typography>Due Date: {invoice.DueDate}</Typography>
                          <Typography>
                            Balance: ${toTwoDecimals(invoice.Balance)}
                          </Typography>
                          <Typography>
                            Email Status: {invoice.EmailStatus}
                          </Typography>
                          <Typography>
                            Bill Email: {invoice.BillEmail.Address}
                          </Typography>
                        </Box>
                        {invoice.BillAddr && (
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: "bold" }}>
                              Billing Address
                            </Typography>
                            <Typography>{invoice.BillAddr.Line1}</Typography>
                            <Typography>{invoice.BillAddr.Line2}</Typography>
                            <Typography>
                              {invoice.BillAddr.City},{" "}
                              {invoice.BillAddr.CountrySubDivisionCode}{" "}
                              {invoice.BillAddr.PostalCode}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <>
                        <Typography>
                          Could not find invoice details on quickbooks. Please
                          check that this invoice was not voided or deleted on
                          Quickbooks
                        </Typography>
                        Link{" "}
                        <a href={invoiceLink}>
                          {salesTicket.invoiceCustomerRefName}
                        </a>
                      </>
                    )}
                  </Box>
                  <Card sx={{ padding: "16px", marginTop: "32px" }}>
                    <Typography
                      variant="h5"
                      sx={{
                        marginBottom: "0px",
                        paddingBottom: "0px",
                      }}
                    >
                      Invoice Transaction History
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Ref No.</TableCell>
                            <TableCell>Deposit to Account</TableCell>
                            <TableCell>Payment Total</TableCell>
                            <TableCell>
                              Amount Applied To This Invoice
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {invoice &&
                            invoice.LinkedTxn &&
                            invoice.LinkedTxn.map((txn, index) => {
                              if (txn.TxnType == "Payment") {
                                return (
                                  <InvoicePayment
                                    key={index}
                                    id={txn.TxnId}
                                    invoice={invoice}
                                    paymentMethods={paymentMethods}
                                    depositAccounts={depositAccounts}
                                  />
                                );
                              }
                            })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                </Box>
              )}
          </Box>
        </Box>
      )}
      <div
        style={{
          position: "fixed",
          bottom: "20px",
          right: "50px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <>
          <Fab
            onClick={(event) => {
              setActionsAnchor(event.currentTarget);
            }}
            variant="extended"
            color="success"
          >
            Actions <KeyboardArrowUp />
          </Fab>
          <Menu
            id="demo-customized-menu"
            slotProps={{
              list: {
                "aria-labelledby": "demo-customized-button",
              },
            }}
            anchorEl={actionsAnchor}
            open={actionsAnchor}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            onClose={handleCloseActionMenu}
          >
            <Box>
              <PrintSalesMenuItem
                salesTicket={salesTicket}
                disableRipple
                // disabled={
                //   !quote.orderStatus ||
                //   quote.orderStatus.name !== "QUOTE_REQUESTED"
                // }
                onClick={() => {
                  handleCloseActionMenu();
                }}
              />

              {salesTicket &&
                !salesTicket.invoiceId &&
                salesTicket.status == "Pending Payment" && (
                  <>
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem
                      disableRipple
                      // disabled={
                      //   !quote.orderStatus ||
                      //   quote.orderStatus.name !== "QUOTE_REQUESTED"
                      // }
                      onClick={() => {
                        handleCloseActionMenu();
                        setCreateInvoiceDialogOpen(true);
                      }}
                    >
                      <Add sx={{ marginRight: "8px" }} />
                      Create Invoice
                    </MenuItem>
                  </>
                )}

              {salesTicket && salesTicket.invoiceId && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <MenuItem
                    // disabled={
                    //   quote.orderStatus.name === "CANCELLED" ||
                    //   quote.orderStatus.name === "CLOSED"
                    // }
                    onClick={() => {
                      handleCloseActionMenu();
                      setReceivePaymentDialogOpen(true);
                    }}
                    disableRipple
                  >
                    <AttachMoney sx={{ marginRight: "8px" }} />
                    Receive Payment (Cash or Check)
                  </MenuItem>
                </>
              )}
              {salesTicket && salesTicket.invoiceId && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <MenuItem
                    // disabled={
                    //   quote.orderStatus.name === "CANCELLED" ||
                    //   quote.orderStatus.name === "CLOSED"
                    // }
                    onClick={() => {
                      handleCloseActionMenu();
                      window.open(invoiceLink, "_blank");
                    }}
                    disableRipple
                  >
                    <AttachMoney sx={{ marginRight: "8px" }} />
                    Receive Payment (Debit, Credit, or ACH)
                  </MenuItem>
                </>
              )}
              {salesTicket && salesTicket.invoiceId && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <MenuItem
                    // disabled={
                    //   quote.orderStatus.name === "CANCELLED" ||
                    //   quote.orderStatus.name === "CLOSED"
                    // }
                    onClick={() => {
                      handleCloseActionMenu();
                      setSendInvoiceDialogOpen(true);
                    }}
                    disableRipple
                  >
                    <Send sx={{ marginRight: "8px" }} />
                    Send Invoice
                  </MenuItem>
                </>
              )}
              {salesTicket &&
                salesTicket.invoiceId &&
                salesTicket.status == "Pending Payment" && (
                  <>
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem
                      // disabled={
                      //   quote.orderStatus.name === "CANCELLED" ||
                      //   quote.orderStatus.name === "CLOSED"
                      // }
                      onClick={() => {
                        handleCloseActionMenu();
                        setConfirmPaidDialogOpen(true);
                      }}
                      disableRipple
                    >
                      <PriceCheck sx={{ marginRight: "8px" }} />
                      Mark Ticket as Paid
                    </MenuItem>
                  </>
                )}

              {salesTicket &&
                !salesTicket.invoiceId &&
                salesTicket.status != "Paid" &&
                salesTicket.status != "Cancelled" && (
                  <>
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem
                      // disabled={
                      //   quote.orderStatus.name === "CANCELLED" ||
                      //   quote.orderStatus.name === "CLOSED"
                      // }
                      onClick={() => {
                        setCancelDialogOpen(true);
                        handleCloseActionMenu();
                      }}
                      disableRipple
                    >
                      <Close sx={{ marginRight: "8px" }} />
                      Cancel Ticket
                    </MenuItem>
                  </>
                )}
            </Box>
          </Menu>
        </>
      </div>

      <CreateInvoiceDialog
        initialData={parsedCreateInvoiceData}
        loading={loading}
        errorMessage={createInvoiceError}
        onSubmit={(invoiceData) => {
          createInvoice({ invoiceData, salesTicketId: salesTicket.id });
        }}
        open={createInvoiceDialogOpen}
        onClose={() => {
          setCreateInvoiceDialogOpen(false);
        }}
      />

      <ReceiveCashOrCheckPaymentDialog
        currentInvoice={invoice}
        open={receivePaymentDialogOpen}
        onClose={() => {
          setReceivePaymentDialogOpen(false);
        }}
        onSuccess={() => {
          openSnackMessage("success", "Payment received!");
          getInvoiceDetails(); // refresh invoice
          setReceivePaymentDialogOpen(false);
        }}
      />
      {invoice && (
        <SendInvoiceDialog
          invoiceId={invoice.Id}
          docNumber={invoice.DocNumber}
          subCustomer={invoice.CustomerRef.name}
          currentBillingEmail={invoice.BillEmail.Address}
          open={sendInvoiceDialogOpen}
          onClose={() => {
            setSendInvoiceDialogOpen(false);
          }}
          onSend={() => {
            setSendInvoiceDialogOpen(false);
            openSnackMessage("success", "Invoice Sent!");
            getInvoiceDetails(); // refresh invoice
          }}
        />
      )}
      {salesTicket && (
        <ConfirmTicketPaidDialog
          salesTicketId={salesTicket.id}
          open={confirmPaidDialogOpen}
          onClose={() => {
            setConfirmPaidDialogOpen(false);
          }}
          onConfirm={() => {
            setConfirmPaidDialogOpen(false);
            openSnackMessage("success", "Payment Status Updated");
            getSalesTicketById(id); // refresh
          }}
        />
      )}
      {salesTicket && (
        <CancelTicketDialog
          salesTicketId={salesTicket.id}
          open={cancelDialogOpen}
          onClose={() => {
            setCancelDialogOpen(false);
          }}
          onConfirm={() => {
            setCancelDialogOpen(false);
            openSnackMessage("success", "Sales Ticket Cancelled");
            getSalesTicketById(id); // refresh
          }}
        />
      )}
    </Box>
  );
};

export default SalesTicket;
